import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import { fxDifference } from '../utils/invoiceCurrency';

function fmtDuration(iso) {
    if (!iso) return '';
    const neg = iso.startsWith('-');
    const s = neg ? iso.slice(1) : iso;
    let totalSec = 0;
    const days = s.match(/(\d+)D/);
    if (days) totalSec += parseInt(days[1]) * 86400;
    const tPart = s.includes('T') ? s.split('T')[1] : '';
    const h = tPart.match(/(\d+)H/);
    const m = tPart.match(/(\d+)M/);
    const sec = tPart.match(/([\d.]+)S/);
    if (h) totalSec += parseInt(h[1]) * 3600;
    if (m) totalSec += parseInt(m[1]) * 60;
    if (sec) totalSec += parseFloat(sec[1]);
    const hh = Math.floor(totalSec / 3600);
    const mm = Math.floor((totalSec % 3600) / 60);
    return `${neg ? '-' : ''}${hh}:${mm.toString().padStart(2, '0')}`;
}

function csvEscape(val) {
    if (val == null) return '';
    const s = String(val);
    return (s.includes(';') || s.includes('"') || s.includes('\n'))
        ? '"' + s.replace(/"/g, '""') + '"'
        : s;
}

function downloadCsv(filename, rows, headers) {
    const lines = [headers.join(';'), ...rows.map(r => r.map(csvEscape).join(';'))];
    const csv = '﻿' + lines.join('\r\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
}

function inDateRange(dateStr, from, to) {
    if (!dateStr) return false;
    if (from && dateStr < from) return false;
    if (to   && dateStr > to)   return false;
    return true;
}

const DATASETS = [
    {
        id: 'users',
        label: 'Benutzer',
        filterType: 'none',
        columns: [
            { key: 'name',      label: 'Name',        default: true  },
            { key: 'username',  label: 'Benutzername', default: true  },
            { key: 'role',      label: 'Rolle',        default: true  },
            { key: 'is_active', label: 'Aktiv',        default: true  },
        ],
    },
    {
        id: 'holidays',
        label: 'Urlaub',
        filterType: 'daterange',
        columns: [
            { key: 'user_name',   label: 'Mitarbeiter', default: true },
            { key: 'start_date',  label: 'Von',         default: true },
            { key: 'end_date',    label: 'Bis',         default: true },
            { key: 'number_days', label: 'Arbeitstage', default: true },
        ],
    },
    {
        id: 'sickdays',
        label: 'Krankheitstage',
        filterType: 'daterange',
        columns: [
            { key: 'user_name',   label: 'Mitarbeiter', default: true },
            { key: 'start_date',  label: 'Von',         default: true },
            { key: 'end_date',    label: 'Bis',         default: true },
            { key: 'number_days', label: 'Arbeitstage', default: true },
        ],
    },
    {
        id: 'allowances',
        label: 'Urlaubsanspruch',
        filterType: 'year',
        columns: [
            { key: 'user_name',       label: 'Mitarbeiter',  default: true  },
            { key: 'username',        label: 'Benutzername', default: false },
            { key: 'base_allowance',  label: 'Grundanspruch',default: true  },
            { key: 'carry_over',      label: 'Übertrag',     default: true  },
            { key: 'extra_days',      label: 'Zusatztage',   default: false },
            { key: 'total_available', label: 'Gesamt',       default: true  },
            { key: 'used',            label: 'Genutzt',      default: true  },
            { key: 'remaining',       label: 'Verbleibend',  default: true  },
        ],
    },
    {
        id: 'overtime',
        label: 'Überstunden',
        filterType: 'year',
        columns: [
            { key: 'user_name',         label: 'Mitarbeiter', default: true  },
            { key: 'year',              label: 'Jahr',        default: true  },
            { key: 'month',             label: 'Monat',       default: true  },
            { key: 'planned_time',      label: 'Soll',        default: true  },
            { key: 'actual_time',       label: 'Ist',         default: true  },
            { key: 'overtime',          label: 'Überstunden', default: true  },
            { key: 'manual_correction', label: 'Korrektur',   default: false },
        ],
    },
    {
        id: 'timetracking',
        label: 'Zeiterfassung',
        filterType: 'daterange',
        userPicker: true,
        columns: [
            { key: 'user_name',     label: 'Mitarbeiter', default: true  },
            { key: 'date',          label: 'Datum',       default: true  },
            { key: 'start_time',    label: 'Start',       default: true  },
            { key: 'end_time',      label: 'Ende',        default: true  },
            { key: 'break_minutes', label: 'Pause (min)', default: true  },
            { key: 'travel_minutes',label: 'Reise (min)', default: false },
            { key: 'net_work_time', label: 'Arbeitszeit', default: true  },
            { key: 'status',        label: 'Status',      default: true  },
        ],
    },
    {
        id: 'invoices',
        label: 'Rechnungen',
        filterType: 'daterange',
        columns: [
            { key: 'invoice_number', label: 'Rechnungsnr.',      default: true  },
            { key: 'recipient_name', label: 'Empfänger',         default: true  },
            { key: 'issue_date',     label: 'Ausstellungsdatum', default: true  },
            { key: 'due_date',       label: 'Fälligkeitsdatum',  default: false },
            { key: 'total_net',      label: 'Netto',             default: true  },
            { key: 'total_gross',    label: 'Brutto',            default: true  },
            { key: 'status',         label: 'Status',            default: true  },
            { key: 'currency',       label: 'Währung',           default: false },
            { key: 'exchange_rate',  label: 'Kurs (Rechnung)',   default: false },
            { key: 'fixed_amount_eur', label: 'Fester EUR-Betrag', default: false },
            { key: 'settled_amount_eur', label: 'Gutschrift EUR', default: false },
            { key: 'settled_on',     label: 'Gutschrift am',     default: false },
            { key: 'fx_difference',  label: 'Kursdifferenz EUR', default: false },
        ],
    },
];

const THIS_YEAR = new Date().getFullYear();

function defaultCols(ds) {
    return Object.fromEntries(ds.columns.map(c => [c.key, c.default]));
}

export default function DataExportPanel({ users }) {
    const [ds, setDs] = useState(DATASETS[0]);
    const [cols, setCols] = useState(() => defaultCols(DATASETS[0]));
    const [dateFrom, setDateFrom] = useState('');
    const [dateTo, setDateTo] = useState('');
    const [year, setYear] = useState(THIS_YEAR);
    const [selectedUserId, setSelectedUserId] = useState('');
    const [userMap, setUserMap] = useState({});
    const [loading, setLoading] = useState(false);
    const [status, setStatus] = useState(null); // { ok, msg }

    useEffect(() => {
        const map = {};
        for (const u of (users || [])) map[u.id] = u.name;
        setUserMap(map);
    }, [users]);

    const switchDs = useCallback(next => {
        setDs(next);
        setCols(defaultCols(next));
        setStatus(null);
        setSelectedUserId('');
    }, []);

    const toggleCol = key => setCols(prev => ({ ...prev, [key]: !prev[key] }));

    const activeCols = ds.columns.filter(c => cols[c.key]);

    const handleExport = async () => {
        if (activeCols.length === 0) {
            setStatus({ ok: false, msg: 'Bitte mindestens eine Spalte auswählen.' });
            return;
        }
        setLoading(true);
        setStatus(null);
        try {
            const rows = await fetchRows(ds, { dateFrom, dateTo, year, selectedUserId, userMap, users: users || [] });
            const headers = activeCols.map(c => c.label);
            const csvRows = rows.map(row => activeCols.map(c => row[c.key]));
            downloadCsv(`${ds.id}_export.csv`, csvRows, headers);
            setStatus({ ok: true, msg: `${rows.length} Zeile${rows.length !== 1 ? 'n' : ''} exportiert` });
        } catch (e) {
            setStatus({ ok: false, msg: e.message || 'Export fehlgeschlagen.' });
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="export-panel">
            <div className="export-ds-row">
                {DATASETS.map(d => (
                    <button
                        key={d.id}
                        className={`export-ds-chip${ds.id === d.id ? ' active' : ''}`}
                        onClick={() => switchDs(d)}
                    >
                        {d.label}
                    </button>
                ))}
            </div>

            <div className="export-body">
                <div className="export-col-block">
                    <div className="export-block-hd">
                        <span className="export-block-label">Spalten</span>
                        <div className="export-quick-links">
                            <button className="export-link-btn" onClick={() => setCols(Object.fromEntries(ds.columns.map(c => [c.key, true])))}>Alle</button>
                            <button className="export-link-btn" onClick={() => setCols(Object.fromEntries(ds.columns.map(c => [c.key, false])))}>Keine</button>
                        </div>
                    </div>
                    <div className="export-col-grid">
                        {ds.columns.map(c => (
                            <label key={c.key} className="export-col-item">
                                <input
                                    type="checkbox"
                                    checked={!!cols[c.key]}
                                    onChange={() => toggleCol(c.key)}
                                />
                                {c.label}
                            </label>
                        ))}
                    </div>
                </div>

                {(ds.filterType !== 'none' || ds.userPicker) && (
                    <div className="export-filter-block">
                        <span className="export-block-label">Filter</span>

                        {ds.filterType === 'daterange' && (
                            <div className="export-filter-fields">
                                <label className="export-filter-label">
                                    Von
                                    <input type="date" className="input" value={dateFrom} onChange={e => setDateFrom(e.target.value)} />
                                </label>
                                <label className="export-filter-label">
                                    Bis
                                    <input type="date" className="input" value={dateTo} onChange={e => setDateTo(e.target.value)} />
                                </label>
                            </div>
                        )}

                        {ds.filterType === 'year' && (
                            <div className="export-filter-fields">
                                <label className="export-filter-label">
                                    Jahr
                                    <input
                                        type="number"
                                        className="input"
                                        value={year}
                                        min="2000"
                                        max="2099"
                                        onChange={e => setYear(Number(e.target.value))}
                                        style={{ width: 96 }}
                                    />
                                </label>
                            </div>
                        )}

                        {ds.userPicker && (
                            <div className="export-filter-fields" style={{ marginTop: ds.filterType !== 'none' ? 8 : 0 }}>
                                <label className="export-filter-label" style={{ minWidth: 200 }}>
                                    Mitarbeiter
                                    <select className="input" value={selectedUserId} onChange={e => setSelectedUserId(e.target.value)}>
                                        <option value="">Alle Mitarbeiter</option>
                                        {(users || []).map(u => (
                                            <option key={u.id} value={u.id}>{u.name}</option>
                                        ))}
                                    </select>
                                </label>
                            </div>
                        )}
                    </div>
                )}
            </div>

            <div className="export-action">
                <button
                    className="button"
                    style={{ background: 'var(--color-primary)', color: '#fff' }}
                    onClick={handleExport}
                    disabled={loading || activeCols.length === 0}
                >
                    {loading ? 'Wird exportiert…' : 'CSV exportieren'}
                </button>
                {status && (
                    <span style={{ color: status.ok ? 'var(--color-success, #16a34a)' : 'var(--color-danger, #dc2626)', fontSize: 'var(--font-size-sm)', fontWeight: 500 }}>
                        {status.msg}
                    </span>
                )}
            </div>
        </div>
    );
}

async function fetchRows(dataset, { dateFrom, dateTo, year, selectedUserId, userMap, users }) {
    switch (dataset.id) {
        case 'users': {
            const res = await api.get('/User');
            return res.data.map(u => ({ name: u.name, username: u.username, role: u.role, is_active: u.is_active ? 'Ja' : 'Nein' }));
        }
        case 'holidays': {
            const res = await api.get('/Holiday?limit=2000');
            return res.data
                .filter(h => !dateFrom && !dateTo ? true : inDateRange(h.start_date, dateFrom, dateTo))
                .map(h => ({ user_name: userMap[h.user_id] || h.user_id, start_date: h.start_date, end_date: h.end_date, number_days: h.number_days }));
        }
        case 'sickdays': {
            const res = await api.get('/SickDay?limit=2000');
            return res.data
                .filter(s => !dateFrom && !dateTo ? true : inDateRange(s.start_date, dateFrom, dateTo))
                .map(s => ({ user_name: userMap[s.user_id] || s.user_id, start_date: s.start_date, end_date: s.end_date, number_days: s.number_days }));
        }
        case 'allowances': {
            const res = await api.get(`/HolidayAllowance/overview/${year}`);
            return res.data.map(a => ({
                user_name: a.user_name, username: a.username, base_allowance: a.base_allowance,
                carry_over: a.carry_over, extra_days: a.extra_days, total_available: a.total_available,
                used: a.used, remaining: a.remaining,
            }));
        }
        case 'overtime': {
            const res = await api.get('/OvertimeAccount?limit=2000');
            return res.data
                .filter(o => o.year === year)
                .map(o => ({
                    user_name: userMap[o.user_id] || o.user_id, year: o.year, month: o.month,
                    planned_time: fmtDuration(o.planned_time), actual_time: fmtDuration(o.actual_time),
                    // "Überstunden" = the net balance shown in the UI (Ist − Soll + manual),
                    // which is what final_balance carries.
                    overtime: fmtDuration(o.final_balance), manual_correction: fmtDuration(o.manual_correction),
                    final_balance: fmtDuration(o.final_balance),
                }))
                .sort((a, b) => {
                    if (a.user_name < b.user_name) return -1;
                    if (a.user_name > b.user_name) return 1;
                    return a.month - b.month;
                });
        }
        case 'timetracking': {
            const fromYear = dateFrom ? parseInt(dateFrom.slice(0, 4), 10) : THIS_YEAR;
            const toYear   = dateTo   ? parseInt(dateTo.slice(0, 4),   10) : fromYear;
            const years = [];
            for (let y = fromYear; y <= Math.min(toYear, fromYear + 5); y++) years.push(y);

            const targets = selectedUserId
                ? users.filter(u => u.id === selectedUserId)
                : users;
            const allRecords = [];
            for (const u of targets) {
                for (const y of years) {
                    try {
                        const res = await api.get(`/Stats/admin/employee/${u.id}/records?year=${y}`);
                        for (const r of res.data) {
                            if (dateFrom || dateTo ? inDateRange(r.date, dateFrom, dateTo) : true) {
                                allRecords.push({
                                    user_name: u.name, date: r.date, start_time: r.start_time, end_time: r.end_time,
                                    break_minutes: r.break_minutes, travel_minutes: r.travel_minutes,
                                    net_work_time: r.net_work_time, status: r.status,
                                });
                            }
                        }
                    } catch { /* user may have no records */ }
                }
            }
            allRecords.sort((a, b) => a.date < b.date ? -1 : a.date > b.date ? 1 : 0);
            return allRecords;
        }
        case 'invoices': {
            const res = await api.get('/Invoice');
            return res.data
                .filter(i => !dateFrom && !dateTo ? true : inDateRange(i.issue_date, dateFrom, dateTo))
                .map(i => ({
                    invoice_number: i.invoice_number, recipient_name: i.recipient_name,
                    issue_date: i.issue_date, due_date: i.due_date,
                    total_net: i.total_net?.toFixed(2), total_gross: i.total_gross?.toFixed(2),
                    status: i.status, currency: i.currency,
                    exchange_rate: i.exchange_rate,
                    fixed_amount_eur: i.fixed_amount_eur?.toFixed(2) ?? '',
                    settled_amount_eur: i.settled_amount_eur?.toFixed(2) ?? '',
                    settled_on: i.settled_on ?? '',
                    // Signed: negative means the bank's conversion came in below the
                    // rate on the invoice. Empty while the invoice is unsettled.
                    fx_difference: fxDifference(i)?.toFixed(2) ?? '',
                }));
        }
        default:
            return [];
    }
}
